import { useCallback, useEffect, useState } from 'react';
import * as api from './parentApi.js';
import { labelFor, NOTABLE } from './eventLabels.js';

/**
 * A one-tap sign-in link for a child's device.
 *
 * The code travels in the query string, which is normally a poor place for a
 * secret — but the studio sends Referrer-Policy: no-referrer, and the sign-in
 * screen strips the parameter with history.replaceState the moment it is read,
 * so it never lands in history or in a shared URL. The alternative is a
 * six-year-old typing 32 characters, which in practice means a parent typing
 * them instead.
 */
const signInLink = (token: string) =>
  `${window.location.origin}/?code=${encodeURIComponent(token)}`;

const when = (at: number) =>
  new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function SignIn({ onDone }: { onDone: () => void }) {
  const [key, setKey] = useState('');
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="p-signin"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setFailed(false);
        try {
          const { token } = await api.login(key);
          api.setParentToken(token);
          onDone();
        } catch {
          setFailed(true);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h1>Parent console</h1>
      <input
        type="password"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="Access key"
        autoComplete="current-password"
      />
      <button disabled={busy || key.length === 0}>{busy ? 'Checking…' : 'Sign in'}</button>
      {failed && <p className="p-bad">That key was not right.</p>}
    </form>
  );
}

export function Parent() {
  const [signedIn, setSignedIn] = useState(() => api.getParentToken() !== null);
  const [overview, setOverview] = useState<api.Overview | null>(null);
  const [log, setLog] = useState<api.AuditEntry[]>([]);
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [issued, setIssued] = useState<{ kid: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [o, l] = await Promise.all([api.getOverview(), api.getAudit(filter)]);
      setOverview(o);
      setLog(l);
    } catch {
      api.clearParentToken();
      setSignedIn(false);
    }
  }, [filter]);

  useEffect(() => {
    if (signedIn) void refresh();
  }, [signedIn, refresh]);

  if (!signedIn) return <main className="p-shell"><SignIn onDone={() => setSignedIn(true)} /></main>;
  if (!overview) return <main className="p-shell"><p>Loading…</p></main>;

  const spendPct = overview.spend.capUsd
    ? Math.min(100, (overview.spend.monthToDateUsd / overview.spend.capUsd) * 100)
    : 0;

  return (
    <main className="p-shell">
      <header className="p-bar">
        <h1>Parent console</h1>
        <button
          className="p-quiet"
          onClick={() => {
            api.clearParentToken();
            setSignedIn(false);
          }}
        >
          Sign out
        </button>
      </header>

      <section className="p-card">
        <h2>This month’s spending</h2>
        <div className="p-gauge">
          <div className="p-gauge__fill" style={{ width: `${spendPct}%` }} />
        </div>
        <p>
          <strong>${overview.spend.monthToDateUsd.toFixed(2)}</strong> of $
          {overview.spend.capUsd.toFixed(2)}. Building stops automatically at the cap.
        </p>
        <button
          className="p-quiet"
          onClick={async () => {
            const next = prompt('New monthly cap in dollars', String(overview.spend.capUsd));
            if (next === null) return;
            const value = Number(next);
            if (!Number.isFinite(value) || value < 0) return;
            await api.setCap(value);
            void refresh();
          }}
        >
          Change the cap
        </button>
      </section>

      <section className="p-kids">
        {overview.kids.map((kid) => (
          <article key={kid.id} className="p-card">
            <div className="p-kid__head">
              <h2>{kid.name}</h2>
              <span className={kid.isActive ? 'p-tag p-tag--on' : 'p-tag p-tag--off'}>
                {kid.isActive ? 'Active' : 'Paused'}
              </span>
            </div>
            <dl className="p-stats">
              <div><dt>Credits</dt><dd>{kid.credits}</dd></div>
              <div>
                <dt>Studio time</dt>
                <dd>{kid.minutesUsedThisWeek} / {kid.weeklyMinutes} min</dd>
              </div>
              <div><dt>Games shipped</dt><dd>{kid.gamesShipped}</dd></div>
              <div>
                <dt>Blocked</dt>
                <dd className={kid.buildsBlocked ? 'p-bad' : undefined}>{kid.buildsBlocked}</dd>
              </div>
            </dl>
            <div className="p-actions">
              <button
                onClick={async () => {
                  await api.setActive(kid.id, !kid.isActive);
                  void refresh();
                }}
              >
                {kid.isActive ? 'Pause the studio' : 'Let them back in'}
              </button>
              <button
                className="p-quiet"
                onClick={async () => {
                  const label = prompt(`What device is this for ${kid.name}?`, 'iPad');
                  if (!label) return;
                  const { token } = await api.issueDeviceToken(kid.id, label);
                  setIssued({ kid: kid.name, token });
                  void refresh();
                }}
              >
                Set up a device
              </button>
              <button
                className="p-quiet"
                onClick={async () => {
                  if (!confirm(`Sign ${kid.name} out on every device?`)) return;
                  await api.revokeTokens(kid.id);
                  void refresh();
                }}
              >
                Sign out everywhere
              </button>
              <button className="p-quiet" onClick={() => setFilter(filter === kid.id ? undefined : kid.id)}>
                {filter === kid.id ? 'Show everyone' : `Only ${kid.name}`}
              </button>
            </div>
          </article>
        ))}
      </section>

      {issued && (
        <section className="p-card p-issued">
          <h2>Set up {issued.kid}’s device</h2>
          <p>
            Send this link to the device, or have them type the code. Either way it is shown{' '}
            <strong>once</strong> — only a one-way hash of it is stored, so it cannot be looked up
            again.
          </p>

          <label className="p-issued__label">Link — send it to their iPad</label>
          <code className="p-issued__link">{signInLink(issued.token)}</code>
          <button
            onClick={() => {
              void navigator.clipboard?.writeText(signInLink(issued.token));
              setCopied(true);
            }}
          >
            {copied ? 'Copied ✓' : 'Copy the link'}
          </button>

          <label className="p-issued__label">Or the code, if they have to type it</label>
          <code>{(issued.token.match(/.{1,4}/g) ?? []).join(' ')}</code>

          <button
            className="p-quiet"
            onClick={() => {
              setIssued(null);
              setCopied(false);
            }}
          >
            Done
          </button>
        </section>
      )}

      <section className="p-card">
        <h2>Everything that happened</h2>
        <ul className="p-log">
          {log.map((entry) => (
            <li key={entry.id} className={NOTABLE.has(entry.event) ? 'p-log__row p-log__row--flag' : 'p-log__row'}>
              <div className="p-log__head">
                <strong>{labelFor(entry.event)}</strong>
                <span>{entry.kidName ?? '—'} · {when(entry.at)}</span>
              </div>
              <details>
                <summary>Details</summary>
                <pre>{JSON.stringify(entry.payload, null, 2)}</pre>
              </details>
            </li>
          ))}
          {log.length === 0 && <li className="p-log__row">Nothing yet.</li>}
        </ul>
      </section>
    </main>
  );
}
